/**
 * 设备类型检测
 *
 * 判定原则：只有「手机」才算移动端，平板一律视为桌面端。
 * 原实现的 UA 正则里 `Android` 与 `iPad` 都没有宽度条件，导致：
 *   - 所有安卓平板（UA 必含 Android）无论多大屏都被判为 mobile；
 *   - iPad 开启"请求移动网站"后同样被判为 mobile；
 * 于是平板访问 /home、/invite 会被强制重定向到 H5 页。此处改为：
 *   - 手机 UA（iPhone / Android + Mobile 等）→ 移动端；
 *   - Android 平板（有 Android 但无 Mobile 标记）、iPad → 按宽度判定；
 *   - 无移动 UA 的触屏设备（iPadOS 默认伪装 macOS UA）→ 按宽度兜底。
 */

/** 低于此宽度才视为移动端（含常见手机横屏宽度，排除主流平板竖屏 744~834px） */
export const MOBILE_MAX_WIDTH = 700;

const hasTouch = (): boolean =>
  'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;

/**
 * 是否为平板设备（安卓平板 / iPad / iPadOS 伪装 macOS 的触屏大屏）
 */
export const isTablet = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;

  // 安卓平板：有 Android 但没有 Mobile 标记（Chrome 在手机上必带 "Mobile"）
  const androidTablet = /Android/i.test(ua) && !/Mobile/i.test(ua);
  // iPad：显式 iPad UA，或 iPadOS 13+ 伪装成 Macintosh 的触屏设备
  const iPad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && hasTouch());

  return (androidTablet || iPad) && window.innerWidth > MOBILE_MAX_WIDTH;
};

/**
 * 检测当前设备是否为移动端（手机）。平板返回 false，走 PC 站。
 */
export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;

  // 平板优先豁免，避免被下面的 UA 规则误判
  if (isTablet()) return false;

  // 明确的手机 UA
  const phoneUA =
    /iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(ua) ||
    (/Android/i.test(ua) && /Mobile/i.test(ua));
  if (phoneUA) return true;

  // 兜底：触屏 + 窄屏（含未命中上面规则的小平板 / 折叠屏）
  return hasTouch() && window.innerWidth <= MOBILE_MAX_WIDTH;
};
